/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useScheduleSettings } from '@sportninja/common/hooks/useScheduleSettings'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { getStatTypesBySportId } from '@sportninja/common/selectors/types'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import {
  Avatar,
  Button,
  Collapse,
  Empty,
  Spin,
  Table,
  Tooltip,
  Typography,
} from 'antd'
import { connect } from 'react-redux'
import ReprocessingSettingsOverlay from 'src/pages/Schedule/ReprocessingSettingsOverlay'
import generateStandingColumns from 'src/utils/generateStandingColumns'
import mapStandingsRow from 'src/utils/mapStandingsRow'
import Icon from '../../Icon'
import GameListFilters from '../../List/GameList/GameListFilters'
import useGetScheduleFiltering from '../../List/useGetScheduleFiltering'
import TieBreakingProcedureModal from './TieBreakingProcedureModal'

// Legacy column definitions - kept for fallback when settings are not available
const hockeyColumns = [
  {
    key: 'GP',
    sorter: (a, b) => a.GP - b.GP,
    title: 'GP',
    dataIndex: 'GP',
    showSorterTooltip: {
      title: 'Games Played',
    },
  },
  {
    key: 'W',
    sorter: (a, b) => a.W - b.W,
    title: 'W',
    dataIndex: 'W',
    showSorterTooltip: {
      title: 'Wins',
    },
  },
  {
    key: 'L',
    sorter: (a, b) => a.L - b.L,
    title: 'L',
    dataIndex: 'L',
    showSorterTooltip: {
      title: 'Losses',
    },
  },
  {
    key: 'T',
    sorter: (a, b) => a.T - b.T,
    title: 'T',
    dataIndex: 'T',
    showSorterTooltip: {
      title: 'Ties',
    },
  },
  {
    key: 'OTL',
    sorter: (a, b) => a.OTL - b.OTL,
    title: 'OTL',
    dataIndex: 'OTL',
    showSorterTooltip: {
      title: 'Overtime Losses',
    },
  },
  {
    key: 'OTW',
    sorter: (a, b) => a.OTW - b.OTW,
    title: 'OTW',
    dataIndex: 'OTW',
    showSorterTooltip: {
      title: 'Overtime Wins',
    },
  },
  {
    key: 'SOL',
    sorter: (a, b) => a.SOL - b.SOL,
    title: 'SOL',
    dataIndex: 'SOL',
    showSorterTooltip: {
      title: 'Shootout Losses',
    },
  },
  {
    key: 'PTS',
    sorter: (a, b) => a.PTS - b.PTS,
    title: 'PTS',
    dataIndex: 'PTS',
    showSorterTooltip: {
      title: 'Points',
    },
  },
  {
    key: 'WPCT',
    sorter: (a, b) => a.WPCT - b.WPCT,
    title: 'WPCT',
    dataIndex: 'WPCT',
    showSorterTooltip: {
      title: 'Winning Percentage',
    },
  },
  {
    key: 'WPCTD',
    sorter: (a, b) => a.WPCTD - b.WPCTD,
    title: 'WPCTD',
    dataIndex: 'WPCTD',
    defaultSortOrder: 'descend',
    showSorterTooltip: {
      title: 'In-Division Winning Percentage',
    },
    width: 80,
  },
  {
    key: 'IN-DIV',
    title: 'DIV',
    dataIndex: 'IN-DIV',
    width: 150,
    showSorterTooltip: {
      // Custom title so we can break it up into multiple lines
      // title: 'In-Division Record (W-L-T-OTL-SOL)',
      title: () => {
        return (
          <span>
            In-Division Record <br />
            (W-L-T-OTL-SOL)
          </span>
        )
      },
    },
    sorter: (a, b) => a['IN-DIV'].split('-')[0] - b['IN-DIV'].split('-')[0],
  },
  {
    key: 'GF',
    sorter: (a, b) => a.GF - b.GF,
    title: 'GF',
    dataIndex: 'GF',
    showSorterTooltip: {
      title: 'Goals For',
    },
  },
  {
    key: 'GA',
    sorter: (a, b) => a.GA - b.GA,
    title: 'GA',
    dataIndex: 'GA',
    showSorterTooltip: {
      title: 'Goals Against',
    },
  },
  {
    key: 'GPCT',
    sorter: (a, b) => a.GPCT - b.GPCT,
    title: 'GPCT',
    dataIndex: 'GPCT',
    showSorterTooltip: {
      title: 'Game Percentage',
    },
  },
  {
    key: 'PIM',
    sorter: (a, b) => a.PIM - b.PIM,
    title: 'PIM',
    dataIndex: 'PIM',
    showSorterTooltip: {
      title: 'Penalty Minutes',
    },
  },
]

const Standings = ({
  areFiltersHidden,
  setIsFilterToggleDisabled = () => {},
  id,
  defaultSort,
  refreshKey,
  scheduleReprocessingListener,
  statWhitelist = [],
  shouldStickyHeader = true,
  hideGroupName = false,
  // Legacy props - kept for backward compatibility but no longer used
  isSoccer = false,
  isInFrame = false,
  isGenericSport = false,
  sportId,
  statTypes,
  isAdmin = false,
  setOutsideDismissed = () => {},
  dismissed = false,
}) => {
  const { scheduleSettings, fetchScheduleSettings } = useScheduleSettings()

  const { standings } = scheduleSettings?.settings ?? {}
  const { sort_order, displayed } = standings ?? {}
  const { stat_id, tie_breaker_stats } = sort_order ?? {}
  const defaultStatType = statTypes?.find((s) => s.id === stat_id)

  // Generate dynamic columns based on settings
  const dynamicColumns = useMemo(() => {
    if (!displayed?.length || !statTypes?.length) {
      // Fallback to hockey columns if no settings available
      return hockeyColumns
    }

    return generateStandingColumns(displayed, statTypes, {
      primaryId: stat_id,
      tieBreakerIds: tie_breaker_stats,
    })
  }, [displayed, statTypes, stat_id, tie_breaker_stats])
  const [isTieBreakingModalVisible, setIsTieBreakingModalVisible] =
    useState(false)

  /**
   * Memoized common columns configuration for the standings table
   * This prevents unnecessary re-renders when other component state changes
   */
  const commonColumns = useMemo(
    () => [
      {
        title: 'TEAM',
        dataIndex: 'team',
        render: (item) => {
          return (
            <Link
              to={`/team/${item?.team?.id}?sn_schedule=${id}`}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <Avatar
                src={
                  getImageThumbnailId(item?.team) ||
                  '/images/placeholders/hockey/team.png'
                }
                shape='square'
                icon={<Icon name='user-friends' fontSize={14} />}
                css={css`
                  border: 1px solid #dbdbdb;
                  background-color: white;
                  padding: 2px;
                `}
              />
              <Typography.Text strong>{item?.team?.name_full}</Typography.Text>
            </Link>
          )
        },
        key: 'team',
        fixed: 'left',
        width: 300,
      },
    ],
    [id]
  ) // Only re-compute when id changes

  const { filters, state, setState, loaded, currentParams } =
    useGetScheduleFiltering({ schedule_id: id }, { defaultSort: 'desc' })
  const [scheduleId, setScheduleId] = useState(currentParams?.sn_division ?? id)
  const [grouping, setGrouping] = useState(filters?.[filters?.length - 1]?.name)
  const [ready, setReady] = useState(false)
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [groups, setGroups] = useState([])
  const groupList = useMemo(() => Object.keys(groups), [groups])
  const activeListItems = useMemo(
    () => groupList.map((_, i) => i.toString()),
    [groupList]
  )
  const [expandAllItems, setExpandAllItems] = useState(true)
  const [activeKey, setActiveKey] = useState(null)

  const shouldDisplayTieBreakingModal = useMemo(() => {
    if (!isCanlan) {
      return true
    }
    // Only show if there the user has admin permissions
    return isAdmin
  }, [isAdmin])

  useEffect(() => {
    if (id && !scheduleSettings) {
      fetchScheduleSettings(id)
    }
  }, [id, scheduleSettings, fetchScheduleSettings])

  useEffect(() => {
    return () => {
      setIsFilterToggleDisabled(false)
    }
  }, [])

  useEffect(() => {
    // Wait for schedule filters to finish loading, then set the grouping and load statistics
    if (loaded) {
      setGrouping(filters?.[filters?.length - 1]?.name || '')
      setReady(true)
      if (filters?.length === 0) {
        setIsFilterToggleDisabled(true)
      }
    }
  }, [loaded, filters?.length])

  const getItems = async (page) => {
    setLoading(true)
    let groupBy
    if (grouping) {
      groupBy = grouping
    }

    // const slug = `/schedules/${scheduleId}/stats/byType`
    const slug = `/schedules/${scheduleId}/standings`

    const response = await req(slug, {
      query: { groupBy, page },
    })

    const g = {}
    response.data.forEach((row) => {
      g[row.schedule.id] = g[row.schedule.id] || {}
      g[row.schedule.id].rows = g[row.schedule.id].rows || []
      g[row.schedule.id].name = row.schedule.name
      if (row?.team !== null) {
        g[row.schedule.id].rows.push(row)
      }
    })

    setItems(response.data)
    setGroups(g)
    setLoading(false)
  }

  useEffect(() => {
    ready && getItems()
  }, [ready, scheduleId, grouping, refreshKey])

  const collapseItems = useMemo(
    () => [
      ...groupList.map((groupId, index) => {
        const group = groups[groupId]
        return {
          key: String(index),
          label: group?.name,
          children: (
            <Table
              columns={commonColumns.concat(dynamicColumns)}
              bordered
              size='small'
              scroll={{ x: 1300 }}
              pagination={false}
              dataSource={group.rows.map(mapStandingsRow)}
            />
          ),
        }
      }),
    ],
    [commonColumns, dynamicColumns, groupList, groups]
  )

  return (
    <>
      {/* <ReprocessingOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
      /> */}
      <ReprocessingSettingsOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
        setOutsideDismissed={setOutsideDismissed}
      />
      <div
        css={css`
          margin-top: 24px;

          ${scheduleReprocessingListener &&
          !dismissed &&
          css`
            opacity: 0.7;
            filter: blur(2px);
            pointer-events: none;
          `}
        `}
      >
        <GameListFilters
          filters={filters}
          state={state}
          setState={setState}
          loading={loading}
          onScheduleChange={(scheduleId, scheduleName) => {
            if (scheduleId) {
              setScheduleId(scheduleId)
              setGrouping(scheduleName)
            } else {
              setScheduleId(id)
              setGrouping(filters?.[filters?.length - 1]?.name || '')
            }
          }}
          isHidden={areFiltersHidden}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex-direction: row;
              gap: 8px;
            `}
          >
            <p
              css={css`
                height: 12px;
                font-size: 12px;
                line-height: 12px;
                margin-bottom: 8px;
                text-transform: capitalize;
              `}
            />
            <Button
              css={
                !isInFrame
                  ? css``
                  : css`
                      /* border should be : rgba(0, 0, 0, 0.3) */
                      border: 1px solid rgba(0, 0, 0, 0.3);
                      color: ${colors.HEADER};
                    `
              }
              type='default'
              onClick={() => {
                if (expandAllItems) {
                  setExpandAllItems(false)
                  setActiveKey(null)
                } else {
                  setExpandAllItems(true)
                  setActiveKey(activeListItems)
                }
              }}
            >
              <span>{expandAllItems ? 'Collapse All' : 'Expand All'}</span>
            </Button>
            {shouldDisplayTieBreakingModal ? (
              <Tooltip title='Tie-Breaking Procedure'>
                <Button
                  css={
                    !isInFrame
                      ? css``
                      : css`
                          border: 1px solid rgba(0, 0, 0, 0.3);
                          color: ${colors.HEADER};
                        `
                  }
                  type='default'
                  icon={<Icon name='info-circle' />}
                  onClick={() => setIsTieBreakingModalVisible(true)}
                />
              </Tooltip>
            ) : null}
          </div>
        </GameListFilters>
        {items?.length === 0 && !loading ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={css`
              margin-top: 24px;
            `}
          />
        ) : null}
        {!ready ||
          (loading && (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                height: 400px;
              `}
            >
              <Spin />
            </div>
          ))}
        {ready && groupList.length > 0 && !loading && (
          <Collapse
            css={
              !isInFrame
                ? css``
                : css`
                    /* if is inFrame, we should change ant-collapse-header-text color to colors.HEADER */
                    .ant-collapse-header-text {
                      color: ${colors.HEADER};
                    }
                    .ant-collapse-expand-icon {
                      color: ${colors.HEADER};
                    }
                  `
            }
            items={collapseItems}
            activeKey={expandAllItems ? activeListItems : activeKey}
            onChange={(e) => {
              setExpandAllItems(false)
              setActiveKey(e)
            }}
            style={{ marginBottom: 24 }}
          />
        )}
        <TieBreakingProcedureModal
          isOpen={isTieBreakingModalVisible}
          onClose={() => setIsTieBreakingModalVisible(false)}
          tieBreakingStats={tie_breaker_stats}
          statTypes={statTypes}
          defaultSortBy={stat_id}
        />
      </div>
    </>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    statTypes: getStatTypesBySportId(state, sportId),
  }
}

const mapDispatchToProps = (_) => {
  return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(Standings)
